import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { pb, isUserValid } from "../services/pocketbase";
import { Link } from "react-router-dom";

const Leaderboard = () => {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoggedIn = isUserValid();

  // Function to fetch clips sorted by upvotes
  const fetchTopClips = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!isLoggedIn) {
        setLoading(false);
        return;
      }

      // Get all clip votes to calculate upvotes
      const voteRecords = await pb.collection("clip_votes").getFullList({
        filter: 'vote_type="upvote"',
        expand: "clip,clip.user,user",
      });

      // Count upvotes per clip
      const clipVoteCounts = {};
      voteRecords.forEach((vote) => {
        if (!vote.expand?.clip) return;

        const clipId = vote.expand.clip.id;
        if (!clipVoteCounts[clipId]) {
          clipVoteCounts[clipId] = {
            count: 0,
            clipData: vote.expand.clip,
            userData: vote.expand.clip.expand?.user || null,
          };
        }
        clipVoteCounts[clipId].count += 1;
      });

      // Convert to array and sort by upvote count
      const sortedClips = Object.values(clipVoteCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10) // Limit to top 10
        .map((item) => ({
          ...item.clipData,
          upvotes: item.count,
          userData: item.userData,
        }));

      // For any clips without user data, fetch it separately
      const clipsWithUserData = await Promise.all(
        sortedClips.map(async (clip) => {
          if (clip.userData) {
            return clip;
          }

          try {
            const userData = await pb.collection("users").getOne(clip.user);
            return {
              ...clip,
              userData,
            };
          } catch (err) {
            console.error("Error fetching user data:", err);
            return clip;
          }
        })
      );

      setClips(clipsWithUserData);
    } catch (err) {
      console.error("Error fetching top clips:", err);
      setError("Failed to load leaderboard. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    fetchTopClips();
  }, [fetchTopClips]);

  // Function to get YouTube video ID from link
  const getVideoId = (youtubeLink) => {
    if (!youtubeLink) return null;

    try {
      // Handle different YouTube URL formats
      let videoId = null;

      if (youtubeLink.includes("youtu.be/")) {
        videoId = youtubeLink.split("youtu.be/")[1].split("?")[0];
      } else if (youtubeLink.includes("youtube.com/watch")) {
        const urlParams = new URLSearchParams(youtubeLink.split("?")[1]);
        videoId = urlParams.get("v");
      } else if (youtubeLink.includes("youtube.com/embed/")) {
        videoId = youtubeLink.split("youtube.com/embed/")[1].split("?")[0];
      }

      return videoId;
    } catch (err) {
      console.error("Error parsing YouTube link:", err);
      return null;
    }
  };

  // Function to get game display name
  const getGameName = (gameId) => {
    const games = {
      blackops6: "Black Ops 6",
      gta5: "GTA 5",
      other: "Other",
    };
    return games[gameId] || gameId;
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading leaderboard...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!isLoggedIn) {
    return (
      <Container sx={{ mt: 4, textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            borderRadius: 2,
          }}
        >
          <Alert
            severity="info"
            sx={{
              mb: 3,
              width: "80%",
              maxWidth: "500px",
              "& .MuiAlert-message": { fontSize: "1.1rem" },
            }}
          >
            Please login to view leaderboard
          </Alert>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            sx={{
              backgroundColor: "#c4d4e0",
              "&:hover": {
                backgroundColor: "#848d94",
              },
            }}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{ mb: 4, textAlign: "center" }}
      >
        TOP CLIPS LEADERBOARD
      </Typography>
      {/* Top 3 Clips */}
      <Box sx={{ mb: 6 }}>
        <Grid container spacing={3} justifyContent="center">
          {/* Second place (left) */}
          {(clips[1] || clips.length === 0) && (
            <Grid
              item
              xs={12}
              md={4}
              key={clips[1]?.id || "second-placeholder"}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: "2px solid silver",
                  bgcolor: "rgba(40, 44, 52, 0.99)",
                  minWidth: "280px",
                }}
              >
                {/* Rank Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: "silver",
                    color: "black",
                    px: 1.5,
                    py: 0.5,
                    fontWeight: "bold",
                    zIndex: 1,
                  }}
                >
                  #2
                </Box>

                {/* Game Badge */}
                {clips[1] && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bgcolor: "rgba(40, 44, 52, 0.8)",
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      zIndex: 1,
                      maxWidth: "70%", // Prevent overlap with rank number
                    }}
                  >
                    <Chip
                      label={getGameName(clips[1]?.game)}
                      size="small"
                      sx={{
                        bgcolor:
                          clips[1]?.game === "blackops6"
                            ? "#c4d4e0"
                            : clips[1]?.game === "gta5"
                            ? "#6cd46c"
                            : "#f5a742",
                        color: "black",
                        fontWeight: "bold",
                      }}
                    />
                  </Box>
                )}

                {/* Video Embed or Placeholder */}
                <Box
                  sx={{
                    position: "relative",
                    paddingTop: "56.25%",
                    overflow: "hidden",
                  }}
                >
                  {clips[1] && getVideoId(clips[1]?.clip_link) ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${getVideoId(
                        clips[1]?.clip_link
                      )}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      title={clips[1]?.clip_title}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <Typography>No video available</Typography>
                    </Box>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom noWrap>
                    {clips[1]?.clip_title || "Waiting for submission"}
                  </Typography>

                  {clips[1] ? (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Avatar
                        src={
                          clips[1]?.userData?.avatar
                            ? `${pb.baseUrl}/api/files/users/${clips[1]?.userData?.id}/${clips[1]?.userData?.avatar}`
                            : null
                        }
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {clips[1]?.userData?.username || "Unknown User"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Be the second to submit a clip!
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Upvotes{" "}
                      <Typography component="span" fontWeight="bold">
                        {clips[1]?.upvotes || "-"}
                      </Typography>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* First place (center) */}
          {(clips[0] || clips.length === 0) && (
            <Grid item xs={12} md={4} key={clips[0]?.id || "first-placeholder"}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: "2px solid gold",
                  bgcolor: "rgba(40, 44, 52, 0.99)",
                  transform: { xs: "scale(1)", md: "scale(1.05)" },
                  zIndex: 2,
                  minWidth: "280px", // Added minimum width to prevent clipping
                }}
              >
                {/* Rank Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: "gold",
                    color: "black",
                    px: 1.5,
                    py: 0.5,
                    fontWeight: "bold",
                    zIndex: 1,
                  }}
                >
                  #1
                </Box>

                {/* Game Badge */}
                {clips[0] && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bgcolor: "rgba(40, 44, 52, 0.8)",
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      zIndex: 1,
                      maxWidth: "70%", // Prevent overlap with rank number
                    }}
                  >
                    <Chip
                      label={getGameName(clips[0]?.game)}
                      size="small"
                      sx={{
                        bgcolor:
                          clips[0]?.game === "blackops6"
                            ? "#c4d4e0"
                            : clips[0]?.game === "gta5"
                            ? "#6cd46c"
                            : "#f5a742",
                        color: "black",
                        fontWeight: "bold",
                      }}
                    />
                  </Box>
                )}

                {/* Video Embed or Placeholder */}
                <Box
                  sx={{
                    position: "relative",
                    paddingTop: "56.25%",
                    overflow: "hidden",
                  }}
                >
                  {clips[0] && getVideoId(clips[0]?.clip_link) ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${getVideoId(
                        clips[0]?.clip_link
                      )}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      title={clips[0]?.clip_title}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <Typography>No video available</Typography>
                    </Box>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom noWrap>
                    {clips[0]?.clip_title || "Waiting for submission"}
                  </Typography>

                  {clips[0] ? (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Avatar
                        src={
                          clips[0]?.userData?.avatar
                            ? `${pb.baseUrl}/api/files/users/${clips[0]?.userData?.id}/${clips[0]?.userData?.avatar}`
                            : null
                        }
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {clips[0]?.userData?.username || "Unknown User"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Be the first to submit a clip!
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Upvotes{" "}
                      <Typography component="span" fontWeight="bold">
                        {clips[0]?.upvotes || "-"}
                      </Typography>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Third place (right) */}
          {(clips[2] || clips.length === 0) && (
            <Grid item xs={12} md={4} key={clips[2]?.id || "third-placeholder"}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  border: "2px solid #cd7f32", // bronze
                  bgcolor: "rgba(40, 44, 52, 0.99)",
                  minWidth: "280px", // Added minimum width to prevent clipping
                }}
              >
                {/* Rank Badge */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    bgcolor: "#cd7f32", // Bronze color
                    color: "black",
                    px: 1.5,
                    py: 0.5,
                    fontWeight: "bold",
                    zIndex: 1,
                  }}
                >
                  #3
                </Box>

                {/* Game Badge */}
                {clips[2] && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bgcolor: "rgba(40, 44, 52, 0.8)",
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      zIndex: 1,
                      maxWidth: "70%", // Prevent overlap with rank number
                    }}
                  >
                    <Chip
                      label={getGameName(clips[2]?.game)}
                      size="small"
                      sx={{
                        bgcolor:
                          clips[2]?.game === "blackops6"
                            ? "#c4d4e0"
                            : clips[2]?.game === "gta5"
                            ? "#6cd46c"
                            : "#f5a742",
                        color: "black",
                        fontWeight: "bold",
                      }}
                    />
                  </Box>
                )}

                {/* Video Embed or Placeholder */}
                <Box
                  sx={{
                    position: "relative",
                    paddingTop: "56.25%",
                    overflow: "hidden",
                  }}
                >
                  {clips[2] && getVideoId(clips[2]?.clip_link) ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${getVideoId(
                        clips[2]?.clip_link
                      )}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      title={clips[2]?.clip_title}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <Typography>No video available</Typography>
                    </Box>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom noWrap>
                    {clips[2]?.clip_title || "Waiting for submission"}
                  </Typography>

                  {clips[2] ? (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Avatar
                        src={
                          clips[2]?.userData?.avatar
                            ? `${pb.baseUrl}/api/files/users/${clips[2]?.userData?.id}/${clips[2]?.userData?.avatar}`
                            : null
                        }
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {clips[2]?.userData?.username || "Unknown User"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Be the third to submit a clip!
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Upvotes{" "}
                      <Typography component="span" fontWeight="bold">
                        {clips[2]?.upvotes || "-"}
                      </Typography>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
      {/* Table of remaining clips */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Other Top Clips
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ bgcolor: "rgba(40, 44, 52, 0.99)" }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Game</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Upvotes</TableCell>
                <TableCell>Video</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clips.slice(3).length > 0 ? (
                clips.slice(3).map((clip, index) => (
                  <TableRow key={clip.id}>
                    <TableCell>{index + 4}</TableCell>
                    <TableCell>
                      <Chip
                        label={getGameName(clip.game)}
                        size="small"
                        sx={{
                          bgcolor:
                            clip.game === "blackops6"
                              ? "#c4d4e0"
                              : clip.game === "gta5"
                              ? "#6cd46c"
                              : "#f5a742",
                          color: "black",
                          fontWeight: "bold",
                        }}
                      />
                    </TableCell>
                    <TableCell>{clip.clip_title}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar
                          src={
                            clip.userData?.avatar
                              ? `${pb.baseUrl}/api/files/users/${clip.userData.id}/${clip.userData.avatar}`
                              : null
                          }
                          sx={{ width: 24, height: 24, mr: 1 }}
                        />
                        {clip.userData?.username || "Unknown User"}
                      </Box>
                    </TableCell>
                    <TableCell>{clip.upvotes}</TableCell>
                    <TableCell>
                      {getVideoId(clip.clip_link) ? (
                        <Button
                          variant="outlined"
                          size="small"
                          href={clip.clip_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Watch
                        </Button>
                      ) : (
                        "No video"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No additional entries yet. Be the first to submit your clip!
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default Leaderboard;
